import { useState } from 'react';
import { EnrichedAccidentReport } from '../../types/accident-reports';
import './reports-list.scss';

interface ReportsListProps {
  reports: EnrichedAccidentReport[];
  count: number;
  language?: 'ar' | 'en' | 'ur';
}

export default function ReportsList({
  reports,
  count,
  language = 'ar',
}: ReportsListProps) {
  const [copiedReportNumber, setCopiedReportNumber] = useState<string | null>(null);

  const texts = {
    ar: {
      title: 'التقارير المتاحة',
      foundReports: 'تم العثور على {count} تقرير',
      reportNumber: 'رقم التقرير',
      reportDate: 'تاريخ التقرير',
      plateNumber: 'رقم اللوحة',
      source: 'المصدر',
      color: 'اللون',
      category: 'الصنف',
      copy: 'نسخ',
      copied: 'تم النسخ!',
    },
    en: {
      title: 'Available Reports',
      foundReports: 'Found {count} report(s)',
      reportNumber: 'Report Number',
      reportDate: 'Report Date',
      plateNumber: 'Plate Number',
      source: 'Source',
      color: 'Color',
      category: 'Category',
      copy: 'Copy',
      copied: 'Copied!',
    },
    ur: {
      title: 'دستیاب رپورٹس',
      foundReports: '{count} رپورٹ(س) ملی',
      reportNumber: 'رپورٹ نمبر',
      reportDate: 'رپورٹ کی تاریخ',
      plateNumber: 'پلیٹ نمبر',
      source: 'ماخذ',
      color: 'رنگ',
      category: 'قسم',
      copy: 'کاپی',
      copied: 'کاپی ہو گیا!',
    },
  };

  const t = texts[language];

  const copyReportNumber = async (reportNumber: string) => {
    try {
      await navigator.clipboard.writeText(reportNumber);
      setCopiedReportNumber(reportNumber);
      setTimeout(() => setCopiedReportNumber(null), 2000);
    } catch (err) {
      console.error('Failed to copy report number:', err);
    }
  };

  return (
    <div className="reports-list-container">
      <div className="reports-header">
        <h3>{t.title}</h3>
        <p className="reports-count">
          {t.foundReports.replace('{count}', count.toString())}
        </p>
      </div>

      <div className="reports-grid">
        {reports.map((report) => (
          <div key={report.ReportNumber} className="report-card">
            <div className="card-header">
              <div className="report-number-section">
                <span className="label">{t.reportNumber}</span>
                <div className="report-number-value">
                  <span className="number">{report.ReportNumber}</span>
                  <button
                    className={`copy-button ${
                      copiedReportNumber === report.ReportNumber ? 'copied' : ''
                    }`}
                    onClick={() => copyReportNumber(report.ReportNumber)}
                    title={t.copy}
                  >
                    <span className="material-symbols-outlined">
                      {copiedReportNumber === report.ReportNumber
                        ? 'check'
                        : 'content_copy'}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">{t.reportDate}:</span>
                <span className="detail-value">{report.FormattedDateTime}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">{t.plateNumber}:</span>
                <span className="detail-value plate-number">
                  {report.PlateNumber}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">{t.source}:</span>
                <span className="detail-value">{report.PlateSourceLabel}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">{t.color}:</span>
                <span className="detail-value">{report.PlateColorLabel}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">{t.category}:</span>
                <span className="detail-value">{report.PlateCategoryLabel}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

